import React, { useCallback, useEffect, useRef, useState } from 'react';
import Header from '@/components/Record/TalkFLow/Header';
import AttachContents from './AttachContents';
import SubHeader from '@/components/Record/TalkFLow/SubHeader';
import Contents, { ContentsRef } from '@/components/Record/TalkFLow/Contents';
import useAi from '@/components/Record/hooks/useAi';
import { useFormContext } from 'react-hook-form';
import { RecordFilter } from '@/types/api';
// import { useAutoFillFirstAiSummary } from '@/components/Record/hooks/useAutoFillFirstAiSummary';
import { useDebounce } from 'usehooks-ts';
import {
  modifyTalkFlow,
  useAiSummeryStatus,
  useGetNewTalkFlowList,
  useReadTalkFlow,
  useTalkFlowDocument,
  useTalkFlowDocumentAiSummary,
} from '@/hooks/apiHooks';
import { filterToParams } from '@/components/Record/util/function';
import useLanguageSetting from '@/hooks/useLanguageSetting';
import { INIT_TALK_FLOW_COUNT_PARAMS } from '@/recoil/init_data';
import { useRecorder } from '@/components/RecorderProvider';
import LayoutLoading from '@/components/Record/LayoutLoading';
import useTalkFlowComment from '@/components/Record/TalkFLow/hooks/useTalkFlowComment';
import CollapsibleLayout, {
  CollapsibleLayoutProps,
} from '@/components/Document/CollapsibleLayout';
import AiChat from '@/components/Record/TalkFLow/AiChat';
import useAutoSummarize from '@/components/Record/TalkFLow/hooks/useAutoSummarize';
import useLatestAiPipelineStatus from '@/components/Record/TalkFLow/hooks/useLatestAiPipelineStatus';
import useRecoverOrganizeContent from '@/components/Record/TalkFLow/hooks/useRecoverOrganizeContent';
import useQueuedRecordRecovery from '@/components/Record/TalkFLow/hooks/useQueuedRecordRecovery';
import useIsScrollDown from '@/hooks/useIsScrollDown';
import useDocumentData from '@/components/Document/@hooks/useDocumentData';
import useBatchPollingAiSummaries from '@/hooks/sse/useBatchPollingAiSummaries';
import { useAtomValue, useSetAtom } from 'jotai';
import { MyInfoState } from '@/recoil/atom';
import {
  DocumentAiSummariesState,
  OrganizeStatusState,
} from '@/recoil/atom/documents';
import { getLexicalNodesFromMarkdown } from '@/components/Document/@hooks/useAiSummaries';
import dayjs from 'dayjs';
import TopToScroll from '@/components/TopToScroll';
import classNames from 'classnames';
import { Box } from '@mui/material';
import styles from '@/public/css/lexical_editor_talkflow.module.scss';
import useResizeObserver from '@/hooks/useResizeObserver';
import useLayoutSize from '@/hooks/useLayoutSize';
import { LayoutType } from '@/constant/layout';
import WordReplacePanel from '@/components/Document/WordReplacePanel';
import { GNBHeightNumber } from '@/components/@IntranetLibrary/GNB/styles';
import useRecordTranslation from '@/components/Record/TalkFLow/hooks/useRecordTranslation';

type P = {
  handleClose?: () => void;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  docId: string;
  setShowScrollToTopButton?: React.Dispatch<React.SetStateAction<boolean>>;
} & Pick<CollapsibleLayoutProps, 'layoutType'>;

const TalkFLow = ({
  docId,
  handleClose,
  scrollRef,
  layoutType,
  setShowScrollToTopButton,
}: P) => {
  const { mutate } = useGetNewTalkFlowList(INIT_TALK_FLOW_COUNT_PARAMS);
  // PC `/talkflow/[documentId]` 와 홈(`/`)의 `TalkFLowModal` 양쪽에서 렌더돼
  // `LANGUAGE_SYNCED_ROUTES` 로 못 옮긴다. 홈에서 모달로 열면 홈의 `isZhEn: false` 를
  // 덮어써 zh 사용자 화면이 en 이 되고, 모달을 닫아도(라우트가 그대로라) 안 돌아온다 — 기존 이슈.
  useLanguageSetting({ isZhEn: true, rnLanguageIsFirst: true });

  const methods = useFormContext<RecordFilter>();
  const { watch, getValues, setValue } = methods;
  const { isOwner } = useAi();
  const content = watch('content');
  const [loading, setLoading] = useState(false);

  const { passportId } = useAtomValue(MyInfoState);
  const setAiSummaries = useSetAtom(DocumentAiSummariesState);

  const { isPausedRecording, isRecordingInProgress, draggable } = useRecorder();

  const isScrollDown = useIsScrollDown({
    scrollRef,
    topStarts: 4,
    hideStarts: 8,
  });

  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useResizeObserver(headerRef, (target) => {
    if (target) {
      setHeaderHeight(target.offsetHeight);
    }
  });

  const { data: documentData, mutate: updateDocument } =
    useTalkFlowDocument(docId);

  // 녹음 파일 추가 시 자동 재정리. content 는 배치폴링(onCompleted)이 채운다.
  // isOrganizing 은 "정리 중" UI(탭 전환/활성화/스켈레톤) 트리거로 사용하고,
  // finish 는 완료/에러 시 배치폴링 콜백에서 호출해 organizing 을 종료한다.
  const {
    isOrganizing,
    finish: finishOrganizing,
    requestManualSummarize,
    retryOrganize,
    requestAutoSummarize,
  } = useAutoSummarize({
    docId,
    isOwner,
  });

  // AI 정리(organize) 실패 상태 — AI 요약과 분리된 본문 전용 에러 배너 트리거.
  // 서버 기준 최신 파이프라인 상태 (SSE 못 받은 기기/새로고침에서도 통일되도록 함께 본다)
  const latestPipelineStatus = useLatestAiPipelineStatus(docId);

  const organizeStatus = useAtomValue(OrganizeStatusState);
  // 로컬 SSE atom 또는 서버 최신 status(error) 중 하나라도 error 면 표시.
  const organizeError =
    organizeStatus[docId]?.status === 'error' ||
    latestPipelineStatus === 'error';

  // 정리 진행 여부는 로컬 isOrganizing 만으로 판단하면 안 된다.
  // 모달을 닫으면 useAutoSummarize 가 언마운트돼 isOrganizing 이 false 로 초기화되지만,
  // 서버 작업은 계속 진행 중이고 그 상태는 atom(uiState==='loading')에 남아 있다.
  // 재진입 시에도 AI 정리 탭을 열린(스켈레톤) 상태로 유지하려면 atom 신호도 함께 본다.
  const aiSummaries = useAtomValue(DocumentAiSummariesState);
  const isSummarizing =
    isOrganizing ||
    latestPipelineStatus === 'running' ||
    Object.values(aiSummaries).some((item) => item.uiState === 'loading');

  const contentsRef = useRef<ContentsRef>(null);
  const onCompleted = useCallback(
    async (summaryText: string) => {
      contentsRef.current?.handleAiOnCreate?.(summaryText);
      finishOrganizing();
      // /summarize 완료 시 문서를 재조회해 commonSummary(AI 요약)를 갱신한다.
      // 추천 제목은 별도 콜백/SSE가 갱신하며 실제 subject 폼 값에는 복사하지 않는다.
      await updateDocument();
    },
    [updateDocument, finishOrganizing],
  );

  const { refresh, initialize } = useDocumentData(docId, methods);

  // 문서 전체 번역 (제목/AI 요약/AI 정리/메모) — 배치 1회 호출.
  const {
    execute: requestTranslation,
    cancel: cancelTranslation,
    isHydrated: isTranslationReady,
  } = useRecordTranslation(docId);

  // 언마운트 시 번역 결과를 지우지 않는다.
  // 같은 문서를 페이지와 모달에서 함께 열 수 있는데, 한쪽을 닫을 때 공용 항목을 지우면
  // 남아 있는 화면의 번역이 같이 사라진다. 결과는 세션 메모리에만 있고 문서별로 나뉘어
  // 있으며(개수 상한은 useRecordTranslation), 원문이 바뀌면 지문 비교가 잡아내므로
  // 남겨두는 편이 안전하다. 같은 문서를 다시 열면 캐시가 재사용돼 재호출도 없다.

  // AI summary 데이터 구독 (TalkFlow 레벨에서 직접 가져오기)
  const { data: serverAiSummaries } = useTalkFlowDocumentAiSummary({
    documentId: docId,
    passportId,
  });

  // 서버에서 AI summary 리스트가 업데이트되면 로컬 상태에 반영
  useEffect(() => {
    if (!serverAiSummaries) return;

    let cancelled = false;

    const syncSummaries = async () => {
      const entries = await Promise.all(
        [...serverAiSummaries]
          .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
          .map(
            async ({
              pipelineId,
              prompt,
              content = '',
              createdAt,
              summarizeType = 'both',
              id: talkFlowDocumentId,
            }) => {
              const contentDom = content
                ? await getLexicalNodesFromMarkdown(content)
                : '';

              return [
                pipelineId,
                {
                  id: pipelineId,
                  docId: talkFlowDocumentId,
                  prompt,
                  content,
                  contentDom,
                  createdAt: dayjs(createdAt),
                  uiState: content
                    ? ('completed' as const)
                    : ('loading' as const),
                  summarizeType,
                },
              ];
            },
          ),
      );

      if (cancelled) return;

      setAiSummaries((prev) => {
        const newSummaries = Object.fromEntries(entries);
        const merged = { ...newSummaries };

        // prev에 있던 항목 중 error/validation_error 상태인 경우 prev 값 유지
        Object.keys(prev).forEach((key) => {
          if (merged[key] && isCompleted(prev[key].uiState)) {
            merged[key] = prev[key];
          }
        });

        return merged;
      });

      // content가 없는 항목들의 실제 상태를 API로 확인
      const needsStatusCheck = serverAiSummaries.filter(
        (item) => !item.content || item.content.trim() === '',
      );

      if (needsStatusCheck.length > 0 && !cancelled) {
        const statusChecks = needsStatusCheck.map(async (item) => {
          try {
            const statusRes = await useAiSummeryStatus(item.pipelineId);
            return { pipelineId: item.pipelineId, status: statusRes.status };
          } catch {
            return null;
          }
        });

        const results = await Promise.all(statusChecks);

        if (!cancelled) {
          setAiSummaries((prev) => {
            const next = { ...prev };
            let changed = false;

            for (const result of results) {
              if (!result || !next[result.pipelineId]) continue;

              const { pipelineId, status } = result;
              let newUiState = next[pipelineId].uiState;

              switch (status) {
                case 'done':
                  newUiState = 'completed';
                  break;
                case 'in_progress':
                  newUiState = 'loading';
                  break;
                case 'error_done':
                  newUiState = 'error';
                  break;
                case 'failover_done':
                case 'validation_error_done':
                  newUiState = 'validation_error';
                  break;
              }

              if (next[pipelineId].uiState !== newUiState) {
                next[pipelineId] = { ...next[pipelineId], uiState: newUiState };
                changed = true;
              }
            }

            return changed ? next : prev;
          });
        }
      }
    };

    syncSummaries();

    return () => {
      cancelled = true;
    };
  }, [serverAiSummaries, setAiSummaries, docId]);

  // AI 정리 폴링 시작 (완료/에러 감지 → onCompleted/onError).
  // onError 는 자동정리 "정리 중" UI 를 종료(finishOrganizing)하는 데 사용.
  useBatchPollingAiSummaries({
    docId,
    onCompleted,
    onError: finishOrganizing,
  });

  // 재접속(스토리지 무관) 복구: 오프라인 중 완료된 정리를 본문/제목에 자동 반영
  useRecoverOrganizeContent(contentsRef, docId);

  // 회의록을 열 때 최신 데이터 가져오기 (마운트 시 1회 실행)
  useEffect(() => {
    refresh();
  }, []);

  // 화면이 내려갈 때(문서 이동으로 key 재마운트 · 모달 닫기) 1초 디바운스를 기다리던 변경분을 바로 저장한다.
  // 아래 initialize() cleanup 이 폼을 비우기 전에 돌도록 그보다 먼저 선언한다. 모달의 close() 가 이미
  // 저장했으면 같은 값이 한 번 더 가는 정도 — 마지막 편집이 유실되는 것보다 낫다 (PORTAL-12584 재QA, AI 리뷰 [높음])
  const latestValuesRef = useRef<RecordFilter | null>(null);
  const lastSavedValuesRef = useRef<RecordFilter | null>(null);
  const isOwnerRef = useRef(isOwner);
  isOwnerRef.current = isOwner;
  useEffect(() => {
    return () => {
      const pending = latestValuesRef.current;
      if (!isOwnerRef.current || !pending) return;
      if (pending === lastSavedValuesRef.current) return;
      if (pending.docId !== docId) return;
      lastSavedValuesRef.current = pending;
      modifyTalkFlow(filterToParams(pending), pending.docId)
        .then(() => updateDocument())
        .catch((e) => console.error(e));
    };
  }, [docId]);

  // 회의록을 닫을 때 초기화 (언마운트 시)
  useEffect(() => {
    return () => {
      initialize();
    };
  }, []);

  //form 상태 변경시 변경 API
  const [formValues, setFormValues] = useState<RecordFilter | null>(null);
  const debouncedValues = useDebounce(formValues, 1000);

  useEffect(() => {
    const subscription = watch((value) => {
      const next = value as RecordFilter;
      // initialize() 의 reset(INIT) 도 여기로 오므로, 이 문서 값일 때만 「저장할 최신 값」으로 기억한다
      if (next.docId === docId) latestValuesRef.current = next;
      setFormValues(next);
    });

    return () => subscription.unsubscribe();
  }, [watch, docId]);

  useEffect(() => {
    // 회의록 값 바뀌면 수정하는 부분
    // TODO: 나중에 모바일쪽이랑도 맞추기
    (async () => {
      if (isOwner && debouncedValues) {
        try {
          lastSavedValuesRef.current = debouncedValues;
          await modifyTalkFlow(
            filterToParams(debouncedValues),
            debouncedValues.docId,
          );
          await updateDocument();
        } catch (e) {
          console.error(e);
        }
      }
    })();
  }, [isOwner, debouncedValues]);

  useEffect(() => {
    draggable(false);

    return () => {
      if (isPausedRecording || isRecordingInProgress) {
        draggable(true);
      }
    };
  }, [isPausedRecording, isRecordingInProgress]);

  useEffect(() => {
    // 읽기처리
    const readTalkFlow = async () => {
      if (docId) {
        try {
          await useReadTalkFlow(docId);
          mutate();
        } catch (e) {
          console.error(e);
        }
      }
    };
    readTalkFlow();
  }, [docId]);

  useEffect(() => {
    // 페이지 포커스 시 일정 시간 경과 후 문서 갱신
    let lastFocusTime = Date.now();

    const handleFocus = async () => {
      const currentTime = Date.now();
      const timeSinceLastFocus = currentTime - lastFocusTime;

      if (timeSinceLastFocus >= 60000) {
        await refresh();
      }

      lastFocusTime = currentTime;
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [refresh]);

  // 업로드하지 못한 녹음 복구 안내 — 문서당 여러 건을 한 번에 (PORTAL-12582)
  useQueuedRecordRecovery(docId, setLoading);

  const { component: TalkFlowComment, commentCount } = useTalkFlowComment();

  // TODO: 나중에 해당 기능 따로 빼기
  const [openPanel, setOpenPanel] = useState<'ai' | 'wordReplace' | null>(null);
  const isDrawerOpen = openPanel !== null;

  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 300);
  const [focusedResultId, setFocusedResultId] = useState<string>('');
  const [scrollTrigger, setScrollTrigger] = useState(0); // 스크롤 강제 트리거용
  const [hoveredResultId, setHoveredResultId] = useState<string | null>(null);

  const handleSearchTextChange = useCallback((text: string) => {
    setSearchText(text);
  }, []);

  const handleScrollToResult = useCallback(
    (resultId: string) => {
      setFocusedResultId(resultId);
      setScrollTrigger((prev) => prev + 1); // 스크롤 강제 트리거
    },
    [focusedResultId],
  );

  const handleHoverResult = useCallback((resultId: string | null) => {
    setHoveredResultId(resultId);
  }, []);

  const handleReplaceContent = useCallback(
    (oldText: string, newText: string, resultId?: string) => {
      const currentContent = getValues('content');

      if (resultId) {
        // 단일 바꾸기: resultId에서 인덱스 추출
        const match = resultId.match(/search-result-(\d+)/);
        if (!match) {
          console.log('Invalid resultId format:', resultId);
          return;
        }

        const clickedIndex = parseInt(match[1], 10);

        // 모든 매칭을 찾아서 개수 확인
        const regex = new RegExp(oldText, 'gi');
        const matches = currentContent.match(regex);
        const totalMatches = matches?.length || 0;

        // 역순으로 변환 (extractSearchResults가 역순이므로)
        const actualIndex = totalMatches - 1 - clickedIndex;

        // 특정 인덱스만 교체
        let replaceCount = 0;
        const newContent = currentContent.replace(regex, (matched) => {
          if (replaceCount === actualIndex) {
            replaceCount++;
            return newText;
          }
          replaceCount++;
          return matched;
        });

        setValue('content', newContent);

        // 하이라이트 갱신을 위해 searchText와 focusedResultId 초기화했다가 다시 설정
        const currentSearchText = searchText;
        setSearchText('');
        setFocusedResultId(''); // 포커스도 초기화
        setTimeout(() => {
          setSearchText(currentSearchText);
        }, 0);
      } else {
        // 모두 바꾸기: 모든 매칭 변경
        const regex = new RegExp(oldText, 'gi');
        const newContent = currentContent.replace(regex, newText);
        setValue('content', newContent);
      }
    },
    [getValues, setValue, searchText],
  );

  const toggleAiPanel = useCallback(() => {
    setOpenPanel((current) => (current === 'ai' ? null : 'ai'));
  }, []);

  const toggleWordReplacePanel = useCallback(() => {
    setOpenPanel((current) =>
      current === 'wordReplace' ? null : 'wordReplace',
    );
  }, []);

  const handleClearFocus = useCallback(() => {
    setFocusedResultId('');
    setHoveredResultId(null);
  }, []);

  useEffect(() => {
    setShowScrollToTopButton?.(!isDrawerOpen);
  }, [setShowScrollToTopButton, isDrawerOpen]);

  const commonClassName = styles.talkFlowEditor;
  const { isMobile } = useLayoutSize(LayoutType.DEFAULT);

  return (
    <Box
      className={`${commonClassName} talkFLow`}
      sx={{
        minWidth: '360px',
        backgroundColor: 'var(--dk-bg, white)',
      }}
    >
      <Header
        ref={headerRef}
        commentCount={commentCount}
        handleClick={handleClose}
        isScrollDown={isScrollDown}
        docId={docId}
        layoutType={layoutType}
        suggestedSubject={documentData?.suggestedSubject}
        onRequestTranslation={requestTranslation}
        onCancelTranslation={cancelTranslation}
        isTranslationReady={isTranslationReady}
      />

      {/* 제목 아래 내용 */}
      <CollapsibleLayout
        isDrawerOpen={isDrawerOpen}
        layoutType={layoutType}
        topOffset={headerHeight}
        menuTopOffset={
          headerHeight + (layoutType === 'page' ? GNBHeightNumber : 0)
        }
        drawerStyle={{
          borderLeft: '1px solid var(--dk-line-strong, #d4d7db)',
          background: 'var(--dk-bg, #fff)',
        }}
        menu={
          <>
            {openPanel === 'ai' && (
              <AiChat
                closeList={toggleAiPanel}
                documentId={docId}
                className={commonClassName}
                isOwner={isOwner}
                onInsertToContent={(text, mode) =>
                  mode === 'overwrite'
                    ? contentsRef.current?.overwriteContent(text)
                    : contentsRef.current?.appendToContent(text)
                }
              />
            )}
            {openPanel === 'wordReplace' && (
              <WordReplacePanel
                closeList={toggleWordReplacePanel}
                documentId={docId}
                className={commonClassName}
                onSearchTextChange={handleSearchTextChange}
                content={content}
                onScrollToResult={handleScrollToResult}
                onHoverResult={handleHoverResult}
                onReplaceContent={handleReplaceContent}
                onClearFocus={handleClearFocus}
              />
            )}
          </>
        }
      >
        {/* 네트워크 연결 및 회의록 작성 안내 */}
        <SubHeader />
        {/* 회의록 관련 정보 */}
        <AttachContents />
        {/* 회의록 */}
        <Contents
          ref={contentsRef}
          docId={docId}
          aiButtonProps={{
            // 자동정리 진행 중 → AI 정리 탭 전환/활성화 + 본문 스켈레톤 트리거
            // (모달 재진입 대비 atom 기반 진행상태까지 반영한 isSummarizing 사용)
            isGenerating: isSummarizing,
            onClick: toggleAiPanel,
            buttonClassName: classNames({
              ai_border: true,
              selected: openPanel === 'ai',
            }),
          }}
          wordReplaceButtonProps={{
            onClick: toggleWordReplacePanel,
            buttonClassName: classNames({
              ai_border: true,
              selected: openPanel === 'wordReplace',
            }),
          }}
          isOwner={isOwner}
          topOffset={headerHeight}
          searchText={debouncedSearchText}
          isWordReplacePanelOpen={openPanel === 'wordReplace'}
          focusedResultId={focusedResultId}
          hoveredResultId={hoveredResultId}
          scrollTrigger={scrollTrigger}
          onCloseWordReplacePanel={toggleWordReplacePanel}
          onReorganize={requestManualSummarize}
          organizeError={organizeError}
          onRetryOrganize={retryOrganize}
          onRequestAutoSummarize={requestAutoSummarize}
        />
        {/* 댓글 영역 */}
        <Box
          sx={{
            paddingBottom: `${120 - (isMobile ? 65 - 24 : 0)}px`,
          }}
        >
          {TalkFlowComment}
        </Box>
      </CollapsibleLayout>
      {loading && <LayoutLoading />}
      {!isDrawerOpen && <TopToScroll style={{ bottom: 90 }} />}
    </Box>
  );
};
export default TalkFLow;

const isCompleted = (uiState?: string) => {
  return (
    uiState && ['completed', 'error', 'validation_error'].includes(uiState)
  );
};
